import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import PlanDocument, { planStatus } from './PlanDocument.jsx';
import PlanAnswerField from './PlanAnswerField.jsx';

function titleFor(type) { return type === 'nutrition' ? 'Suggested Nutrition Plan' : 'Suggested Workout Plan'; }

export default function PlanWorkspace({ planId, onLoaded }) {
  const [payload, setPayload] = useState(null);
  const [answers, setAnswers] = useState({});
  const [content, setContent] = useState(null);
  const [chat, setChat] = useState('');
  const [revision, setRevision] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [confirmDeliver, setConfirmDeliver] = useState(false);
  const [parentReviewUrl, setParentReviewUrl] = useState('');

  async function load() {
    const data = await api.get(`/api/plans/${planId}`);
    setPayload(data);
    setAnswers(data.plan.intake_answers || {});
    setContent(data.plan.plan_content || null);
    onLoaded?.(data);
  }

  useEffect(() => { load().catch((e) => setError(e.message)); }, [planId]);

  const missingLabels = useMemo(() => {
    if (!payload) return [];
    const map = Object.fromEntries((payload.required_fields || []).map((field) => [field.key, field.label]));
    return (payload.plan.missing_fields || []).map((key) => map[key] || key);
  }, [payload]);

  async function action(name, fn) {
    setBusy(name); setError(''); setNote('');
    try {
      const result = await fn();
      if (result?.plan) {
        setPayload((current) => ({ ...current, plan: result.plan }));
        setAnswers(result.plan.intake_answers || {});
        setContent(result.plan.plan_content ?? null);
      }
      return result;
    } catch (e) { setError(e.body?.error || e.message); return null; }
    finally { setBusy(''); }
  }

  async function saveBrief() {
    const previousStatus = payload?.plan?.status;
    const result = await action('brief', () => api.patch(`/api/plans/${planId}/intake`, { answers }));
    if (result) setNote(result.plan.status === 'draft' && ['ready', 'approved', 'delivered'].includes(previousStatus)
      ? 'The athlete’s information changed. This suggested plan is back in Draft and must be regenerated before delivery.'
      : 'Planning brief saved.');
  }

  async function sendChat() {
    const message = chat.trim(); if (!message) return;
    setChat('');
    const result = await action('chat', () => api.post(`/api/plans/${planId}/chat`, { message }, { timeoutMs: 45_000 }));
    if (!result) setChat(message);
  }

  async function requestPlayer() {
    const result = await action('request', () => api.post(`/api/plans/${planId}/request-player`, {}));
    if (result) setNote(result.email?.sent ? 'Player-question email sent.' : `Questions opened in the Player Portal. ${result.email?.error || 'Email was not sent.'}`);
  }

  async function generate() {
    // Full structured documents can use the server's 180 s Claude ceiling;
    // leave 30 s for cold-start, validation, and the database write.
    const result = await action('generate', () => api.post(`/api/plans/${planId}/generate`, {}, { timeoutMs: 210_000 }));
    if (result) setNote('Draft generated. Review every section before approval.');
  }

  async function revise() {
    const instruction = revision.trim(); if (!instruction) return;
    const result = await action('revise', () => api.post(`/api/plans/${planId}/revise`, { instruction }, { timeoutMs: 210_000 }));
    if (result) { setRevision(''); setNote('Skip revised the draft.'); }
  }

  async function saveContent() {
    const result = await action('content', () => api.patch(`/api/plans/${planId}/content`, { plan_content: content }));
    if (result) setNote('Manual edits saved.');
  }

  async function approve() {
    const result = await action('approve', () => api.post(`/api/plans/${planId}/approve`, {}));
    if (result) {
      setParentReviewUrl(result.parent_review_url || '');
      setNote(result.email?.sent ? 'Staff review complete. Parent-review email sent.' : `Staff review complete. Parent review is pending. ${result.email?.error || ''}`);
    }
  }

  async function retryParentReview() {
    const result = await action('parent-email', () => api.post(`/api/plans/${planId}/retry-parent-review`, {}));
    if (result) {
      setParentReviewUrl(result.parent_review_url || '');
      setNote(result.email?.sent ? 'Parent-review email sent.' : `Parent review remains pending. ${result.email?.error || 'Email was not sent.'}`);
    }
  }

  async function copyParentLink() {
    if (!parentReviewUrl) return;
    await navigator.clipboard.writeText(parentReviewUrl);
    setNote('Private parent-review link copied.');
  }

  async function deliver() {
    const result = await action('deliver', () => api.post(`/api/plans/${planId}/deliver`, {}));
    setConfirmDeliver(false);
    if (result) setNote(result.email?.sent ? 'Plan delivered and email sent.' : `Plan delivered in the portal. ${result.email?.error || 'Email was not sent.'}`);
  }

  async function retryEmail() {
    const result = await action('email', () => api.post(`/api/plans/${planId}/retry-email`, {}));
    if (result) setNote(`Plan email sent to ${result.sent_to}.`);
  }

  async function linkTask() {
    const result = await action('link-task', () => api.post(`/api/plans/${planId}/link-task`, {}));
    if (result) {
      setPayload((current) => ({ ...current, plan: result.plan, linked_task: result.linked_task }));
      setNote('Plan linked to its Call Sheet task.');
    }
  }

  if (!payload) return <div className="card">{error || 'Loading Plan Builder…'}</div>;
  const { plan, recruit, required_fields: fields } = payload;
  const locked = ['approved', 'delivered', 'superseded'].includes(plan.status);

  return (
    <div className="space-y-6">
      <section className="card plan-no-print">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-mid-green">{titleFor(plan.plan_type)}</p>
            <h2 className="font-display font-black text-2xl text-hunter">{recruit.first_name} {recruit.last_name}</h2>
            <p className="text-sm text-silver-mute mt-1">{plan.start_date} through {plan.expires_on} · {plan.duration_weeks} weeks · {plan.season_phase}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-sage-100 px-3 py-1 text-xs font-bold text-hunter">{planStatus(plan)}</span>
            <Link to={`/recruits/${plan.recruit_id}?tab=plans&plan=${plan.id}`} className="text-sm text-hunter underline">Profile tab</Link>
          </div>
        </div>
        {error && <p className="mt-4 rounded bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</p>}
        {note && <p className="mt-4 rounded bg-green-50 border border-green-200 p-3 text-sm text-green-800">{note}</p>}
      </section>

      {plan.parent_review_status === 'changes_requested' && <section className="card plan-no-print border border-amber-300 bg-amber-50"><h3 className="font-display font-black text-lg text-amber-900">Parent requested changes</h3><p className="mt-2 text-sm text-amber-900 whitespace-pre-wrap">{plan.parent_review_note}</p><p className="mt-2 text-xs text-amber-800">This suggested plan is back in Draft. Update the brief, regenerate it, and send the new version through review.</p></section>}

      {!locked && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 plan-no-print">
          <section className="card">
            <h3 className="font-display font-black text-xl text-hunter">Planning brief</h3>
            <p className="text-sm text-silver-mute mt-1">The same required information is considered for every {titleFor(plan.plan_type).toLowerCase()}.</p>
            {missingLabels.length > 0 && <div className="mt-4 rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900"><strong>{missingLabels.length} missing:</strong> {missingLabels.join(', ')}</div>}
            <div className="mt-4 space-y-4 max-h-[38rem] overflow-y-auto pr-1">
            {(fields || []).map((field) => <PlanAnswerField key={field.key} field={field} answers={answers} onChange={(key, value) => setAnswers((current) => {
              const next = { ...current };
              if (value === undefined) delete next[key]; else next[key] = value;
              return next;
            })} />)}
            </div>
            <button type="button" onClick={saveBrief} disabled={!!busy} className="btn-secondary mt-4">{busy === 'brief' ? 'Saving…' : 'Save brief'}</button>
          </section>

          <section className="card flex flex-col min-h-[34rem]">
            <h3 className="font-display font-black text-xl text-hunter">Talk with Skip</h3>
            <p className="text-sm text-silver-mute mt-1">Skip asks naturally and fills the structured brief. You remain in control of generation and delivery.</p>
            <div className="mt-4 flex-1 rounded-lg bg-silver-a p-4 space-y-3 overflow-y-auto max-h-[34rem]">
              {(plan.staff_messages || []).length === 0 && <p className="text-sm text-silver-mute">Tell Skip what you already know about this athlete. He’ll work through what is still missing.</p>}
              {(plan.staff_messages || []).map((message, index) => <div key={index} className={`max-w-[88%] rounded-lg px-4 py-3 text-sm whitespace-pre-wrap ${message.role === 'user' ? 'ml-auto bg-hunter text-white' : 'bg-white border border-gray-200 text-silver-text'}`}>{message.content}</div>)}
            </div>
            <div className="mt-4 flex gap-2"><textarea value={chat} onChange={(e) => setChat(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} placeholder="Message Skip…" className="input min-h-20 resize-none" /><button type="button" onClick={sendChat} disabled={!!busy || !chat.trim()} className="btn-primary self-end">{busy === 'chat' ? 'Thinking…' : 'Send'}</button></div>
          </section>
        </div>
      )}

      {!locked && !plan.plan_content && (
        <section className="card plan-no-print flex flex-wrap items-center gap-3">
          <button type="button" onClick={requestPlayer} disabled={!!busy || missingLabels.length === 0} className="btn-secondary">{busy === 'request' ? 'Opening questions…' : 'Request player answers'}</button>
          <button type="button" onClick={generate} disabled={!!busy || missingLabels.length > 0} className="btn-primary">{busy === 'generate' ? 'Building draft…' : `Generate ${titleFor(plan.plan_type)}`}</button>
          <p className="text-xs text-silver-mute">Player email remains hard-paused outside approved live mode.</p>
        </section>
      )}

      <section className="card plan-no-print">
        <h3 className="font-display font-black text-lg text-hunter">Linked service task</h3>
        {payload.linked_task
          ? <p className="mt-2 text-sm text-silver-text">{payload.linked_task.recruit_deliverables?.service_name} · {payload.linked_task.task_label} · due {payload.linked_task.deadline_date || 'not set'}</p>
          : <div className="mt-2"><p className="text-sm text-amber-800">No unambiguous open Workout/Nutrition Plan task is linked. Delivery will stay blocked until the Call Sheet is resolved.</p><button type="button" onClick={linkTask} disabled={!!busy} className="btn-secondary mt-3">{busy === 'link-task' ? 'Checking…' : 'Link open Call Sheet task'}</button></div>}
      </section>

      {content && <PlanDocument plan={{ ...plan, plan_content: content }} playerName={`${recruit.first_name} ${recruit.last_name}`} editable={plan.status === 'ready'} onChange={setContent} />}

      {content && <section className="card plan-no-print space-y-4">
        {plan.status === 'ready' && <><div className="flex flex-wrap gap-3"><button type="button" onClick={saveContent} disabled={!!busy} className="btn-secondary">{busy === 'content' ? 'Saving…' : 'Save manual edits'}</button><button type="button" onClick={approve} disabled={!!busy} className="btn-primary">{busy === 'approve' ? 'Starting parent review…' : 'Approve for parent review'}</button></div><div><label className="block text-sm font-bold text-hunter mb-1">Ask Skip for a revision</label><div className="flex gap-2"><textarea value={revision} onChange={(e) => setRevision(e.target.value)} className="input min-h-20" placeholder="Describe exactly what should change…" /><button type="button" onClick={revise} disabled={!!busy || !revision.trim()} className="btn-secondary self-end">{busy === 'revise' ? 'Revising…' : 'Revise'}</button></div></div></>}
        {plan.status === 'approved' && plan.parent_review_status === 'not_requested' && <button type="button" onClick={retryParentReview} disabled={!!busy} className="btn-primary">{busy === 'parent-email' ? 'Starting review…' : 'Start parent/guardian review'}</button>}
        {plan.status === 'approved' && plan.parent_review_status === 'pending' && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4"><p className="font-bold text-amber-900">Waiting for parent/guardian review</p><p className="mt-1 text-sm text-amber-900">The athlete cannot see this plan yet. The review page asks for the athlete’s last name before revealing it.</p><div className="flex flex-wrap gap-3 mt-3">{parentReviewUrl && <button type="button" onClick={copyParentLink} className="btn-secondary">Copy private review link</button>}<button type="button" onClick={retryParentReview} disabled={!!busy} className="btn-secondary">{busy === 'parent-email' ? 'Sending…' : 'Send a new review link'}</button></div></div>}
        {plan.status === 'approved' && plan.parent_review_status === 'approved' && !confirmDeliver && <button type="button" onClick={() => setConfirmDeliver(true)} className="btn-primary">Deliver to athlete</button>}
        {plan.status === 'approved' && plan.parent_review_status === 'approved' && confirmDeliver && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4"><p className="text-sm font-bold text-amber-900">Deliver this parent-approved suggested plan to {recruit.first_name} and complete its linked Call Sheet task?</p><div className="flex gap-3 mt-3"><button type="button" onClick={deliver} disabled={!!busy} className="btn-primary">{busy === 'deliver' ? 'Delivering…' : 'Yes, deliver plan'}</button><button type="button" onClick={() => setConfirmDeliver(false)} className="btn-secondary">Cancel</button></div></div>}
        {plan.status === 'delivered' && <div className="flex flex-wrap gap-3"><button type="button" onClick={() => window.print()} className="btn-primary">Print plan</button>{!plan.email_sent_at && <button type="button" onClick={retryEmail} disabled={!!busy} className="btn-secondary">{busy === 'email' ? 'Sending…' : 'Retry athlete email'}</button>}</div>}
      </section>}
    </div>
  );
}
